import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock, Edit, Check, Send, Package, Loader2, Upload, ExternalLink,
  ChevronDown, ChevronUp, Image, FileText, X, CreditCard, Truck,
  ArrowRight, Eye, AlertCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../services/api';

/* ───── WORKFLOW STEPS ───── */
const STEPS = [
  { key: 'pending', label: 'Demande recue', icon: Clock, color: 'amber' },
  { key: 'in_progress', label: 'En cours', icon: Edit, color: 'blue' },
  { key: 'bat_sent', label: 'BAT envoyes', icon: Image, color: 'violet' },
  { key: 'bat_approved', label: 'BAT valides', icon: Check, color: 'emerald' },
  { key: 'quote_sent', label: 'Devis envoye', icon: FileText, color: 'orange' },
  { key: 'deposit_paid', label: 'Acompte', icon: CreditCard, color: 'cyan' },
  { key: 'paid', label: 'Paye', icon: Check, color: 'green' },
  { key: 'delivered', label: 'Livre', icon: Truck, color: 'slate' },
];

const stepIndex = (status) => STEPS.findIndex(s => s.key === status);

const NEXT_ACTION = {
  pending: 'Uploadez les BAT pour chaque produit',
  in_progress: 'Continuez a uploader les BAT restants',
  bat_sent: "En attente de validation par l'organisateur",
  bat_approved: 'Configurez le paiement et envoyez le devis',
  quote_sent: "En attente du paiement de l'organisateur",
  deposit_paid: 'Acompte recu — en attente du solde',
  paid: 'Marquez la commande comme livree',
  delivered: 'Commande terminee',
};

/* ───── WORKFLOW STEPPER ───── */
const WorkflowStepper = ({ status }) => {
  const current = stepIndex(status);
  return (
    <div className="flex items-center w-full gap-0 overflow-x-auto pb-1" data-testid="workflow-stepper">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            <div className={`flex flex-col items-center min-w-[56px] sm:min-w-[72px] ${active ? 'scale-105' : ''} transition-transform`}>
              <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-sm border-2 transition-all
                ${done ? 'bg-green-500 border-green-500 text-white' : active ? `bg-${step.color}-500 border-${step.color}-500 text-white shadow-md` : 'bg-slate-100 border-slate-200 text-slate-400'}`}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={`text-[9px] sm:text-[10px] mt-1 text-center leading-tight font-heading uppercase tracking-wide
                ${done ? 'text-green-600 font-bold' : active ? `text-${step.color}-600 font-bold` : 'text-slate-400'}`}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 min-w-[8px] mt-[-12px] ${i < current ? 'bg-green-400' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ───── MAIN COMPONENT ───── */
const ProviderSelectionsPanel = ({ selections, fetchSelections }) => {
  const [expandedSel, setExpandedSel] = useState(null);
  const [batDialog, setBatDialog] = useState(null);
  const [batImages, setBatImages] = useState([]);
  const [batUploading, setBatUploading] = useState(false);
  const batInputRef = useRef(null);
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentMode, setPaymentMode] = useState('50_50');
  const [savingPayment, setSavingPayment] = useState(false);

  const handleBatImageUpload = async (files) => {
    if (!files || files.length === 0) return;
    setBatUploading(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        const arrayBuffer = await file.arrayBuffer();
        const CHUNK_SIZE = 256 * 1024;
        const totalChunks = Math.ceil(arrayBuffer.byteLength / CHUNK_SIZE);
        const uploadId = Date.now().toString(36) + Math.random().toString(36).slice(2);
        let res;
        for (let i = 0; i < totalChunks; i++) {
          const chunk = arrayBuffer.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
          let binary = ''; for (let j = 0; j < chunk.length; j++) binary += String.fromCharCode(chunk[j]);
          const b64 = btoa(binary);
          res = await api.post('/upload/chunk', { filename: file.name, chunk_index: i, total_chunks: totalChunks, upload_id: uploadId, data: b64 }, { timeout: 30000 });
        }
        if (res.data.url) uploaded.push(res.data.url);
      }
      setBatImages(prev => [...prev, ...uploaded]);
    } catch (e) {
      toast.error('Erreur upload');
    } finally {
      setBatUploading(false);
    }
  };

  const handleSaveBat = async () => {
    if (!batDialog || batImages.length === 0) return;
    try {
      await api.put(`/provider/selections/${batDialog.selectionId}/bat/${batDialog.productIndex}`, { bat_images: batImages });
      toast.success('BAT enregistre avec succes !');
      setBatDialog(null);
      setBatImages([]);
      fetchSelections();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleSavePaymentConfig = async () => {
    if (!paymentDialog) return;
    setSavingPayment(true);
    try {
      const res = await api.put(`/provider/selections/${paymentDialog}/payment-config`, { payment_mode: paymentMode });
      if (res.data.payment_link) {
        toast.success(`Lien SumUp genere ! Montant: ${res.data.first_amount}€`);
      } else {
        toast.success('Configuration enregistree');
      }
      setPaymentDialog(null);
      fetchSelections();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSavingPayment(false);
    }
  };

  const handlePaymentStatus = async (selectionId, action) => {
    try {
      await api.put(`/provider/selections/${selectionId}/payment-status`, { action });
      toast.success('Statut mis a jour !');
      fetchSelections();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const stats = {
    pending: selections.filter(s => s.status === 'pending').length,
    bat: selections.filter(s => ['in_progress', 'bat_sent'].includes(s.status)).length,
    approved: selections.filter(s => s.status === 'bat_approved').length,
    payment: selections.filter(s => ['deposit_paid', 'paid', 'quote_sent', 'delivered'].includes(s.status)).length,
  };

  return (
    <div data-testid="provider-selections-panel" className="space-y-6">
      {/* ── HEADER ── */}
      <div>
        <h3 className="font-heading font-bold text-lg sm:text-xl uppercase tracking-wide">Demandes de personnalisation</h3>
        <p className="text-sm text-slate-500 mt-1">Gerez les demandes des organisateurs : uploadez les BAT, envoyez les devis, suivez les paiements.</p>
      </div>

      {/* ── STATS ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'En attente', count: stats.pending, icon: Clock, bg: 'bg-amber-50 border-amber-200', text: 'text-amber-600' },
          { label: 'BAT en cours', count: stats.bat, icon: Image, bg: 'bg-violet-50 border-violet-200', text: 'text-violet-600' },
          { label: 'BAT valides', count: stats.approved, icon: Check, bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-600' },
          { label: 'Paiement / Livraison', count: stats.payment, icon: CreditCard, bg: 'bg-green-50 border-green-200', text: 'text-green-600' },
        ].map((s, i) => (
          <div key={i} className={`${s.bg} border p-4 sm:p-5 flex items-center gap-3`}>
            <s.icon className={`w-6 h-6 sm:w-7 sm:h-7 ${s.text} flex-shrink-0`} />
            <div>
              <span className="font-heading font-black text-2xl sm:text-3xl block leading-none">{s.count}</span>
              <span className="text-xs text-slate-500 font-heading uppercase">{s.label}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── SELECTIONS LIST ── */}
      {selections.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 sm:p-16 text-center">
          <FileText className="w-14 h-14 mx-auto mb-4 text-slate-200" />
          <p className="font-heading font-bold text-base uppercase mb-1">Aucune demande</p>
          <p className="text-sm text-slate-400">Les demandes des organisateurs apparaitront ici.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {selections.map((sel) => {
            const isExpanded = expandedSel === sel.selection_id;
            const current = stepIndex(sel.status);
            const stepInfo = STEPS[current] || STEPS[0];
            const StepIcon = stepInfo.icon;
            const nextAction = NEXT_ACTION[sel.status] || '';
            const allBat = sel.products?.every(p => p.bat_images?.length > 0);

            return (
              <motion.div
                key={sel.selection_id}
                className="bg-white border border-slate-200 overflow-hidden hover:border-slate-300 transition-colors"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`prov-sel-${sel.selection_id}`}
              >
                {/* ── Card Header ── */}
                <div
                  className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
                  onClick={() => setExpandedSel(isExpanded ? null : sel.selection_id)}
                >
                  <div className="flex items-start gap-3 sm:gap-4">
                    {/* Logo */}
                    {sel.logo_url ? (
                      <img src={sel.logo_url} alt="Logo" className="w-12 h-12 sm:w-14 sm:h-14 object-contain border bg-white flex-shrink-0" />
                    ) : (
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-slate-100 border flex items-center justify-center flex-shrink-0">
                        <Package className="w-6 h-6 text-slate-300" />
                      </div>
                    )}

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 bg-${stepInfo.color}-100 text-${stepInfo.color}-700`}>
                          <StepIcon className="w-3.5 h-3.5" /> {stepInfo.label}
                        </span>
                        <span className="font-heading font-bold text-sm sm:text-base">{sel.organizer_name || 'Organisateur'}</span>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                        <span>{sel.products?.length || 0} produit(s)</span>
                        <span className="font-heading font-bold text-brand">{sel.quote?.total_ttc?.toFixed(2) || '0.00'}€ TTC</span>
                        <span>{sel.created_at ? new Date(sel.created_at).toLocaleDateString('fr-FR') : ''}</span>
                      </div>
                    </div>

                    {/* Expand */}
                    <div className="flex-shrink-0 mt-1">
                      {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                    </div>
                  </div>

                  {/* Next action hint */}
                  {nextAction && sel.status !== 'delivered' && (
                    <div className="mt-3 flex items-center gap-2 bg-slate-50 border border-slate-100 px-3 py-2 text-xs text-slate-600">
                      <ArrowRight className="w-4 h-4 text-brand flex-shrink-0" />
                      <span><strong className="text-slate-800">Prochaine etape :</strong> {nextAction}</span>
                    </div>
                  )}
                </div>

                {/* ── Expanded Detail ── */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 sm:px-5 pb-5 border-t border-slate-100 pt-4 space-y-5">

                        {/* Stepper */}
                        <WorkflowStepper status={sel.status} />

                        {/* Logo organisateur */}
                        {sel.logo_url && (
                          <div className="flex items-center gap-4 bg-slate-50 border border-slate-100 p-4">
                            <img src={sel.logo_url} alt="Logo organisateur" className="w-16 h-16 sm:w-20 sm:h-20 object-contain border bg-white p-1" />
                            <div>
                              <p className="text-sm font-heading font-bold uppercase">Logo de l'organisateur</p>
                              <p className="text-xs text-slate-500 mt-0.5">A integrer sur chaque produit pour la personnalisation</p>
                            </div>
                          </div>
                        )}

                        {/* Products */}
                        <div>
                          <h5 className="font-heading font-bold text-xs uppercase text-slate-400 mb-2 tracking-wide">Produits a personnaliser</h5>
                          <div className="space-y-2">
                            {sel.products?.map((p, i) => (
                              <div key={i} className="flex items-center gap-3 sm:gap-4 bg-white border border-slate-200 p-3 sm:p-4 hover:bg-slate-50/50 transition-colors" data-testid={`prov-sel-product-${i}`}>
                                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-slate-50 border overflow-hidden flex-shrink-0">
                                  {p.image_url ? <img src={p.image_url} alt="" className="w-full h-full object-contain p-1" /> : <Package className="w-8 h-8 text-slate-200 m-auto mt-4" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-heading font-bold truncate">{p.product_name}</p>
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    {p.quantity} x {p.unit_price?.toFixed(2)}€/u
                                    {p.tier_label && <span className="text-amber-600 ml-1">({p.tier_label})</span>}
                                  </p>
                                  <p className="text-xs font-heading font-bold text-brand mt-0.5">{p.line_total?.toFixed(2)}€</p>
                                  {p.color && <span className="text-[10px] text-slate-400 block mt-0.5">Couleur : {p.color}</span>}
                                </div>
                                <div className="flex-shrink-0">
                                  {p.bat_images?.length > 0 ? (
                                    <div className="flex items-center gap-2">
                                      <img src={p.bat_images[0]} alt="BAT" className="w-12 h-12 sm:w-14 sm:h-14 object-contain border bg-white" />
                                      <span className="text-xs text-emerald-600 font-bold flex items-center gap-1"><Check className="w-4 h-4" /> BAT</span>
                                    </div>
                                  ) : (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="text-xs h-9 gap-1.5 border-brand/30 text-brand hover:bg-brand/5 hover:border-brand"
                                      onClick={(e) => { e.stopPropagation(); setBatDialog({ selectionId: sel.selection_id, productIndex: i }); setBatImages([]); }}
                                      data-testid={`upload-bat-${sel.selection_id}-${i}`}
                                    >
                                      <Upload className="w-4 h-4" /> Uploader BAT
                                    </Button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Devis */}
                        {sel.quote && (
                          <div className="bg-asphalt text-white p-4 sm:p-5">
                            <h5 className="font-heading font-bold text-xs uppercase tracking-wide text-slate-400 mb-3">Devis automatise</h5>
                            <div className="space-y-1.5">
                              <div className="flex justify-between text-sm"><span>Total HT</span><span className="font-mono font-bold">{sel.quote.total_ht?.toFixed(2)}€</span></div>
                              <div className="flex justify-between text-xs text-slate-400"><span>TVA ({sel.quote.tva_rate}%)</span><span className="font-mono">{sel.quote.tva_amount?.toFixed(2)}€</span></div>
                              <div className="flex justify-between text-lg font-bold border-t border-slate-600 pt-2 mt-2">
                                <span className="font-heading uppercase">Total TTC</span>
                                <span className="font-heading text-brand">{sel.quote.total_ttc?.toFixed(2)}€</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {sel.notes && (
                          <div className="bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                            <span><strong>Note de l'organisateur :</strong> {sel.notes}</span>
                          </div>
                        )}

                        {/* ── ACTIONS ── */}
                        <div className="space-y-2 pt-2 border-t border-slate-100">
                          {sel.status === 'bat_approved' && (
                            <Button
                              className="w-full sm:w-auto bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6"
                              onClick={() => { setPaymentDialog(sel.selection_id); setPaymentMode(sel.payment_mode || '50_50'); }}
                              data-testid={`set-payment-${sel.selection_id}`}
                            >
                              <CreditCard className="w-4 h-4" /> Configurer paiement et envoyer devis
                            </Button>
                          )}

                          {sel.status === 'quote_sent' && !sel.deposit_paid && (
                            <Button className="w-full sm:w-auto bg-cyan-600 hover:bg-cyan-700 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6" onClick={() => handlePaymentStatus(sel.selection_id, sel.payment_mode === 'full' ? 'fully_paid' : 'deposit_paid')}>
                              <Check className="w-4 h-4" /> {sel.payment_mode === 'full' ? 'Confirmer paiement integral' : 'Confirmer acompte 50%'}
                            </Button>
                          )}

                          {sel.status === 'deposit_paid' && !sel.fully_paid && (
                            <Button className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6" onClick={() => handlePaymentStatus(sel.selection_id, 'fully_paid')}>
                              <Check className="w-4 h-4" /> Confirmer solde recu
                            </Button>
                          )}

                          {sel.status === 'paid' && (
                            <Button className="w-full sm:w-auto bg-slate-700 hover:bg-slate-800 text-white font-heading font-bold uppercase text-sm gap-2 h-11 px-6" onClick={() => handlePaymentStatus(sel.selection_id, 'delivered')}>
                              <Truck className="w-4 h-4" /> Marquer comme livre
                            </Button>
                          )}

                          {sel.payment_mode && (
                            <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500 pt-1">
                              <span>Mode : <strong className="text-slate-700">{sel.payment_mode === '50_50' ? '50/50' : 'Integral'}</strong></span>
                              {sel.payment_link && (
                                <a href={sel.payment_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand font-bold hover:underline">
                                  <ExternalLink className="w-3.5 h-3.5" /> Lien SumUp
                                </a>
                              )}
                              {sel.deposit_paid && <span className="text-green-600 font-bold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Acompte recu</span>}
                              {sel.fully_paid && <span className="text-green-600 font-bold flex items-center gap-1"><Check className="w-3.5 h-3.5" /> Paye integralement</span>}
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── BAT UPLOAD DIALOG ── */}
      <Dialog open={!!batDialog} onOpenChange={(open) => { if (!open) { setBatDialog(null); setBatImages([]); } }}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-base">Uploader le BAT</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Uploadez les visuels du produit personnalise avec le logo de l'organisateur integre. L'organisateur validera ensuite ces visuels.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {batImages.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {batImages.map((img, i) => (
                  <div key={i} className="relative aspect-square bg-slate-50 border overflow-hidden group">
                    <img src={img} alt="" className="w-full h-full object-contain p-2" />
                    <button className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => setBatImages(prev => prev.filter((_, idx) => idx !== i))}>
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div>
              <input ref={batInputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleBatImageUpload(e.target.files)} />
              <button
                className="w-full h-28 border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-brand/50 transition-colors flex flex-col items-center justify-center gap-2 text-slate-500 cursor-pointer"
                onClick={() => batInputRef.current?.click()}
                disabled={batUploading}
              >
                {batUploading ? (
                  <><Loader2 className="w-6 h-6 animate-spin text-brand" /><span className="text-xs font-heading uppercase">Upload en cours...</span></>
                ) : (
                  <><Upload className="w-6 h-6" /><span className="text-xs font-heading uppercase">Cliquez ou glissez vos visuels ici</span><span className="text-[10px] text-slate-400">PNG, JPG, WEBP - plusieurs fichiers possibles</span></>
                )}
              </button>
            </div>
            <Button
              className="w-full bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm h-11"
              onClick={handleSaveBat}
              disabled={batImages.length === 0}
              data-testid="save-bat-btn"
            >
              <Check className="w-4 h-4 mr-2" /> Enregistrer le BAT
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── PAYMENT CONFIG DIALOG ── */}
      <Dialog open={!!paymentDialog} onOpenChange={(open) => { if (!open) setPaymentDialog(null); }}>
        <DialogContent className="max-w-md sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase text-base">Configurer le paiement</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              Choisissez le mode de paiement. Un lien SumUp sera genere automatiquement et envoye a l'organisateur.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div>
              <Label className="text-xs font-heading uppercase text-slate-500 mb-3 block">Mode de paiement</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  className={`p-5 border-2 text-left transition-all ${paymentMode === '50_50' ? 'border-brand bg-brand/5 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setPaymentMode('50_50')}
                  data-testid="payment-mode-50"
                >
                  <span className="font-heading font-bold text-base block">50% / 50%</span>
                  <span className="text-xs text-slate-500 mt-1 block">50% a la commande, solde a la livraison</span>
                </button>
                <button
                  className={`p-5 border-2 text-left transition-all ${paymentMode === 'full' ? 'border-brand bg-brand/5 shadow-sm' : 'border-slate-200 hover:border-slate-300'}`}
                  onClick={() => setPaymentMode('full')}
                  data-testid="payment-mode-full"
                >
                  <span className="font-heading font-bold text-base block">Integral</span>
                  <span className="text-xs text-slate-500 mt-1 block">Paiement en totalite a la commande</span>
                </button>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 p-3 text-xs text-blue-800 flex items-start gap-2">
              <CreditCard className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <span>Un lien de paiement <strong>SumUp</strong> sera genere automatiquement avec le montant calcule. L'organisateur recevra une notification.</span>
            </div>
            <Button
              className="w-full bg-brand hover:bg-brand/90 text-white font-heading font-bold uppercase text-sm gap-2 h-11"
              onClick={handleSavePaymentConfig}
              disabled={savingPayment}
              data-testid="save-payment-config-btn"
            >
              {savingPayment ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Generer le lien et envoyer le devis
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProviderSelectionsPanel;
